import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import type { MemberRow } from './types.js';

interface MemberRowActionsProps {
  row: MemberRow;
  onMakeAdmin: (userId: number) => void;
  onRemove: (userId: number) => void;
  onRevoke: (inviteId: number) => void;
  onResend: (inviteId: number) => void;
}

const iconButtonSx: SxProps<Theme> = {
  width: 28,
  height: 28,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  color: 'text.secondary',
  '&:hover': {
    borderColor: 'border.strong',
    color: 'text.primary',
    bgcolor: 'background.page',
  },
  '& .MuiSvgIcon-root': { fontSize: 15 },
};

const dangerIconButtonSx: SxProps<Theme> = {
  ...iconButtonSx,
  '&:hover': {
    borderColor: 'error.main',
    color: 'error.main',
    bgcolor: 'background.paper',
  },
};

export function MemberRowActions({
  row,
  onMakeAdmin,
  onRemove,
  onRevoke,
  onResend,
}: MemberRowActionsProps): ReactNode {
  const isPending = row.status === 'invited' && row.inviteId !== null;
  const isActiveMember = row.status === 'active' && row.userId !== null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
      {isPending ? (
        <>
          <IconButton
            title="Resend invite"
            data-testid={`button-resend-${row.key}`}
            onClick={() => {
              if (row.inviteId !== null) onResend(row.inviteId);
            }}
            sx={iconButtonSx}
          >
            <MailOutlinedIcon />
          </IconButton>
          <IconButton
            title="Revoke invite"
            data-testid={`button-revoke-${row.key}`}
            onClick={() => {
              if (row.inviteId !== null) onRevoke(row.inviteId);
            }}
            sx={dangerIconButtonSx}
          >
            <CloseIcon />
          </IconButton>
        </>
      ) : null}
      {isActiveMember && row.canEdit && row.role !== 'admin' ? (
        <IconButton
          title="Make admin"
          data-testid={`button-make-admin-${row.key}`}
          onClick={() => {
            if (row.userId !== null) onMakeAdmin(row.userId);
          }}
          sx={iconButtonSx}
        >
          <VerifiedUserOutlinedIcon />
        </IconButton>
      ) : null}
      {isActiveMember ? (
        <IconButton
          title="Remove user"
          data-testid={`button-remove-${row.key}`}
          onClick={() => {
            if (row.userId !== null) onRemove(row.userId);
          }}
          sx={dangerIconButtonSx}
        >
          <DeleteOutlinedIcon />
        </IconButton>
      ) : null}
    </Box>
  );
}
